import { useContext } from "react";
import {
  Text,
  Heading,
  Form,
  TextField,
  TextArea,
  Picker,
  PickerItem,
  Link,
} from "@react-spectrum/s2";

import { VideoWizardContext } from "@app/views/projects/videoWizardContext";
import LinkOutIcon from "icon:@app/assets/S2_LinkOutSize100.svg";
import { PersonaSelect } from "./PersonaSelect";
import { VerticalSelect } from "./VerticalSelect";
import { Step } from "./Step";

import { style, size } from "@react-spectrum/s2/style" with { type: "macro" };
import { headingStyle } from "../style" with { type: "macro" };

export const CustomerDetailsStep = ({ onContinue, onBack, continueLabel }) => {
  const {
    template,
    templateId,
    customerName,
    setCustomerName,
    product,
    setProduct,
    hint,
    setHint,
    include3DDigitalTwin,
    setInclude3DDigitalTwin,
    updateVideo,
    isCustomerDetailsDisabled,
    verticals,
    selectedVertical,
    setSelectedVertical,
    personas,
    selectedPersona,
    setSelectedPersona,
  } = useContext(VideoWizardContext);

  return (
    <>
      <Step styles={style({ gridArea: "content" })}>
        <Step.Header
          title="Who is this story for?"
          subtitle={`We’ll generate brand-specific content for your ${template.data?.name ?? ""} story`}
        />

        <div
          className={style({
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            gap: 96,
          })}
        >
          <div
            className={style({
              display: "grid",
              gridTemplateColumns: `minmax(0, ${size(460)}) repeat(auto-fit, minmax(0, auto))`,
              justifyContent: "center",
              alignItems: "center",
              gap: 96,
            })}
          >
            <Form
              onSubmit={() => !isCustomerDetailsDisabled && onContinue?.()}
              UNSAFE_style={{ rowGap: size(32) }}
            >
              <div>
                <div className={style(headingStyle())}>Brand name</div>
                <TextField
                  aria-label="Brand name"
                  placeholder="e.g. Nike, Chase"
                  description="Enter the name of an existing brand"
                  isRequired
                  value={customerName}
                  onChange={setCustomerName}
                />
              </div>
              {templateId === "one-adobe" && (
                <div>
                  <div className={style(headingStyle({ size: "xs" }))}>
                    Featured product or service (optional)
                  </div>
                  <TextField
                    aria-label="Featured product or service"
                    placeholder="e.g. Nike Air Max, Chase Ultimate Rewards"
                    description="Leave blank, and we'll suggest one"
                    necessityIndicator="label"
                    value={product}
                    onChange={setProduct}
                  />
                </div>
              )}

              <div>
                <div className={style(headingStyle({ size: "xs" }))}>
                  Story direction hints (optional)
                </div>
                <TextArea
                  aria-label="Story direction hints"
                  placeholder="e.g. Target Gen Z in the US, focus on holiday shopping, position the offering as a premium choice"
                  description="Add any preferences for the campaign featured in the story, such as target audience, campaign topic, or product positioning."
                  necessityIndicator="label"
                  value={hint}
                  onChange={setHint}
                />
              </div>
            </Form>
          </div>

          {!!template.data?.verticals?.length && (
            <VerticalSelect
              templateId={templateId}
              verticals={verticals}
              selectedVertical={selectedVertical}
              setSelectedVertical={setSelectedVertical}
            />
          )}

          {!!template.data?.personas?.length && (
            <PersonaSelect
              personas={personas}
              selectedPersona={selectedPersona}
              setSelectedPersona={setSelectedPersona}
              previewUrl={template?.data?.personaPreviewUrl}
            />
          )}

          {templateId === "firefly-enterprise" && (
            <div className={style({ width: 360, maxWidth: "full" })}>
              <Heading styles={style(headingStyle())}>
                <Text>3D digital twin</Text>
              </Heading>

              <p
                className={style({
                  font: "body-sm",
                  marginTop: 0,
                  marginBottom: 24,
                })}
              >
                Adds a 3D model preview of your product inside the animation.
                Turn off if your campaign isn&apos;t built around a physical
                product.
              </p>

              <Picker
                label="Include 3D digital twin"
                value={include3DDigitalTwin ? "yes" : "no"}
                onChange={(value) => setInclude3DDigitalTwin(value === "yes")}
                isDisabled={updateVideo.isPending}
              >
                <PickerItem id="yes">Yes</PickerItem>
                <PickerItem id="no">No</PickerItem>
              </Picker>
              <div
                className={style({
                  display: "flex",
                  flexDirection: "row",
                  gap: 8,
                  marginTop: 12,
                  font: "body-sm",
                })}
              >
                {!!template.data?.include3DigitalTwinScenePreviewUrl && (
                  <>
                    <Text>See what the 3D digital twin scene looks like </Text>
                    <Link
                      href={template.data.include3DigitalTwinScenePreviewUrl}
                      target="_blank"
                      isStandalone
                      staticColor="auto"
                    >
                      <Text>Preview</Text>
                      <LinkOutIcon
                        styles={style({ size: 8, marginStart: 8 })}
                      />
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </Step>

      <Step.Actions
        onBack={onBack}
        onContinue={onContinue}
        continueLabel={continueLabel}
        isContinueDisabled={isCustomerDetailsDisabled}
      />
    </>
  );
};
